// Approval-modal mode & acknowledgment resolver.
//
// Implements UX §8.2.1, §8.3-§8.5, §8.6.7, PRD §5.9.2, PRD §5.9.4.
//
// Source-of-truth wording for every checkbox is here. The design's modal
// JSX deviates from §8.4/§8.5 (e.g., uses "I understand" for Standard);
// the spec wins per CLAUDE.md precedence (PRD/UX over design).

import type { Account, ApprovalMode, Pending } from "../types";
import { fmtUSD } from "./format";

export type AckKey =
  | "standard_risk" // Standard: "I accept the $X risk on this trade."
  | "careful_risk" // Careful: "I understand this trade can lose up to $X if the stop fills."
  | "careful_gap" // Careful (swing): "I understand the overnight gap could lose up to $Y."
  | "careful_diverge" // Careful (AI/Det diverge): "I have read both AI and deterministic rationales..."
  | "live_real_money" // Live (any non-Express mode): "I am placing this trade with REAL money..."
  | "pf_gap_intervention"; // PF gap > 30%: extra ack

export type RequiredAck = {
  key: AckKey;
  text: string;
  // Visual emphasis tier; "danger" used for live + PF gap to nudge attention.
  emphasis?: "default" | "danger";
};

// Compute the effective mode after applying invariants:
//   - Live + Express is forbidden (PRD §5.9.2 + UX §8.2.1) → upgrade to Standard
//   - PF gap > 30% upgrades to Careful regardless (PRD §5.9.4)
export const resolveMode = (t: Pending): ApprovalMode => {
  let mode: ApprovalMode = t.mode;
  if (mode === "express" && t.account === "live") {
    mode = "standard";
  }
  if ((t.pfGapPct ?? 0) > 30) {
    mode = "careful";
  }
  return mode;
};

export const resolveAcks = (
  t: Pending,
  mode: ApprovalMode,
): RequiredAck[] => {
  const acks: RequiredAck[] = [];

  if (mode === "standard") {
    acks.push({
      key: "standard_risk",
      text: `I accept the ${fmtUSD(t.risk)} risk on this trade.`,
    });
  }

  if (mode === "careful") {
    acks.push({
      key: "careful_risk",
      text: `I understand this trade can lose up to ${fmtUSD(t.risk)} if the stop fills.`,
    });
    if (t.timeframe === "Swing" && t.gapRisk > 0) {
      acks.push({
        key: "careful_gap",
        text: `I understand the overnight gap could lose up to ${fmtUSD(t.gapRisk)}.`,
      });
    }
    if (t.careful?.aiDivergence) {
      acks.push({
        key: "careful_diverge",
        text:
          "I have read both AI and deterministic rationales and accept this trade.",
      });
    }
  }

  // Live additive (PRD §5.9.2 + UX §8.6.7) — required on every live-account
  // approval regardless of mode. Express + live is impossible per resolveMode.
  if (t.account === "live" && mode !== "express") {
    acks.push({
      key: "live_real_money",
      text: `I am placing this trade with REAL money at account ${t.accountLabel}.`,
      emphasis: "danger",
    });
  }

  // PF-gap intervention (PRD §5.9.4) — extra ack naming the gap percent.
  if ((t.pfGapPct ?? 0) > 30) {
    const x = Math.round(t.pfGapPct ?? 0);
    acks.push({
      key: "pf_gap_intervention",
      text: `Your paper version of this strategy is performing materially better than your live version. The gap is ${x}% and is not explained by sample size. Most users in this position have higher conviction than their live results justify. Continue?`,
      emphasis: "danger",
    });
  }

  return acks;
};

// Destination-indicator copy per UX §8.6.7. Never reveal the broker
// account_id in full; the visible label (e.g. "Trading Live") and equity
// are sufficient context.
export const destinationLabel = (account: Account, mode: ApprovalMode): string => {
  // Use sufficient digits of equity per the spec example. The mode
  // parameter is reserved for future variations (e.g., Express omitting
  // the equity number); kept signature-stable so callers don't change.
  void mode;
  if (account.kind === "trading-live") {
    return `⚠ TRADING LIVE ($${account.equity.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} equity)  REAL MONEY`;
  }
  if (account.kind === "trading-paper") {
    return `TRADING PAPER ($${account.equity.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} equity) · paper`;
  }
  return `INVESTMENT (${account.broker}) · long-term`;
};

// AI/Det divergence relationship label per UX §8.6.3 (mirrors the
// dashboard helper but kept local to the modal so the strings can drift
// independently if the spec wording does).
export const divergenceText = (
  ai: number,
  det: number,
): { text: string; tier: "agree" | "differ" | "diverge" } => {
  const d = Math.abs(ai - det);
  if (d <= 15) {
    return { text: "(agree)", tier: "agree" };
  }
  if (d <= 25) {
    return { text: "(differ)", tier: "differ" };
  }
  return { text: "⚠ diverge", tier: "diverge" };
};
